import firebase from '../firebase/compat'
import { db } from '../firebase/config'
import { client } from '../hooks/Client'
import resyncQualifyingAfterRestore from './questionnaire/resyncQualifyingAfterRestore'

/**
 * @typedef {object} DeletedItem
 * @property {string} docid - Document-id van de regel in DeletedItems
 * @property {string} Collection - Collectie waar het onderdeel in staat
 * @property {string} DocID - Document-id van het onderdeel zelf
 * @property {string} [Title] - Wat de gebruiker ziet
 * @property {string} [ParentID] - Bij een cascade: het ID van de ouder
 */

/**
 * Zet één verwijderd onderdeel terug, en bij een cascade alles wat eraan hing.
 *
 * De drie velden worden echt verwijderd in plaats van op `false` gezet. Het
 * leesfilter kijkt weliswaar naar de waarde, maar een Firestore-`orderBy` laat
 * documenten zónder dat veld juist buiten het resultaat vallen — een document
 * met `Deleted: false` gedraagt zich dus net anders dan een document dat nooit
 * verwijderd is geweest.
 * AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
 * en docs/plans/prullenbak.md
 *
 * @param {DeletedItem} item - De regel uit de prullenbak
 * @returns {Promise<boolean>} of er iets is teruggezet
 */
const restoreDeletedItem = async (item) => {
    if (!item?.Collection || !item?.DocID) return false

    const leegmaken = {
        Deleted: firebase.firestore.FieldValue.delete(),
        DeletedAt: firebase.firestore.FieldValue.delete(),
        DeletedBy: firebase.firestore.FieldValue.delete(),
    }

    await db.collection(item.Collection).doc(item.DocID).update(leegmaken)

    // Hing er een cascade aan dit onderdeel, dan komt die in één keer mee:
    // een huishouden terugzetten zonder zijn notities levert een half
    // huishouden op. Alleen `softDeleteDoc` legt die band vast, dus dit werkt
    // niet voor alles — vandaar dat de kinderen ook los in de lijst staan.
    const kinderen = await db.collection('DeletedItems')
        .where('CompagnyID', '==', client)
        .where('ParentID', '==', item.DocID)
        .get()

    for (const kind of kinderen.docs) {
        const data = kind.data()
        if (!data?.Collection || !data?.DocID) continue
        await db.collection(data.Collection).doc(data.DocID).update(leegmaken)
        await kind.ref.delete()
    }

    // De aantekening zelf hoort daarna te verdwijnen. Opzoeken op DocID en niet
    // op een meegegeven id: de lijst komt uit de collecties zelf, en sommige
    // documenten dragen een eigen `docid`-veld dat hier niets mee te maken heeft.
    const eigenRegel = await db.collection('DeletedItems')
        .where('CompagnyID', '==', client)
        .where('DocID', '==', item.DocID)
        .get()

    for (const regel of eigenRegel.docs) await regel.ref.delete()

    // Een teruggezet meetmoment of een teruggezette kwalificeringsvraag kan
    // veranderen op welk meetmoment de kwalificeringsvragen horen. Het
    // terugzetten is dan al gelukt; mislukt alleen dit, dan zegt de prullenbak
    // niet dat het terugzetten mislukte, maar komt de fout in de console.
    // AI-CONTEXT: See docs/pitfalls.md#api-error-claims-nothing-was-saved
    try {
        await resyncQualifyingAfterRestore(item, client)
    } catch (error) {
        console.error('Kwalificeringsvragen herberekenen na terugzetten mislukt', error)
    }

    return true
}

export default restoreDeletedItem
